<template>
    <div>
        <nav class="bg-white text-black rounded-xl p-4 lg:p-6 mb-4 md:flex items-center gap-4">
            <div class="relative flex-1">
                <label for="jobs-search" class="sr-only">Search roles</label>

                <input
                    id="jobs-search"
                    v-model="search"
                    type="search"
                    placeholder="Search by role, keyword or category..."
                    class="block w-full pl-10 pr-4 py-2 rounded-full font-normal placeholder-black/60 bg-white text-black appearance-none border border-black focus:outline-none text-sm lg:text-base"
                />

                <search-icon class="absolute block w-4 h-auto left-4 align-y" />
            </div>

            <div class="flex gap-4 mt-4 md:mt-0">
                <label for="jobs-location" class="sr-only">Location</label>
                <select id="jobs-location" v-model="location" class="form-select max-md:flex-1 md:min-w-48">
                    <option :value="null">All locations</option>
                    <option v-for="option in locations" :key="option.value" :value="option.value">{{ option.label }}</option>
                </select>

                <label for="jobs-category" class="sr-only">Category</label>
                <select id="jobs-category" v-model="category" class="form-select max-md:flex-1 md:min-w-48">
                    <option :value="null">Categories</option>
                    <option v-for="option in categories" :key="option.value" :value="option.value">{{ option.label }}</option>
                </select>
            </div>
        </nav>

        <slot
            :is-visible="isVisible"
            :active-job-id="activeJobId"
            :toggle-job="toggleJob"
        />

        <p v-if="!visibleJobIds.size" class="bg-white text-black rounded-xl p-6 lg:p-8 font-light">
            No roles match your search. Try a different keyword, location or category.
        </p>
    </div>
</template>

<script setup>
    import { computed, ref } from 'vue';
    import SearchIcon from '@/img/icons/magnifying-glass-regular.svg?component';

    const props = defineProps({
        jobs: {
            type: Array,
            required: true,
        },
        locations: {
            type: Array,
            required: true,
        },
        categories: {
            type: Array,
            required: true,
        },
    });

    const search = defineModel('search', {
        type: String,
        default: '',
    });

    const location = ref(null);
    const category = ref(null);
    const activeJobId = ref(null);

    const matchesSearch = job => {
        const terms = search.value.trim().toLowerCase();

        if (!terms) {
            return true;
        }

        return [
            job.title,
            job.summary,
            job.category,
            job.locationLabel,
            job.employmentType,
        ].join(' ').toLowerCase().includes(terms);
    };

    const visibleJobIds = computed(() => new Set(props.jobs
        .filter(job => !location.value || job.location === location.value)
        .filter(job => !category.value || job.categoryId === category.value)
        .filter(matchesSearch)
        .map(job => job.id)));

    const isVisible = id => visibleJobIds.value.has(id);

    const toggleJob = id => {
        activeJobId.value = activeJobId.value === id ? null : id;
    };
</script>
